import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, LogOut, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { title: "", subtitle: "", event_date: "", booking_link: "", badge: "", price: "", terms: "", lines: "" };

export default function AdminPage() {
    const [token, setToken] = useState(localStorage.getItem("ua_admin_token") || "");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [banners, setBanners] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [type, setType] = useState("event");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [ratings, setRatings] = useState({ zomato: { rating: "", count: "" }, google: { rating: "", count: "" } });
    const [savingRatings, setSavingRatings] = useState(false);

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const loadBanners = async () => {
        const { data } = await axios.get(`${API}/admin/banners`, auth);
        setBanners(data);
    };

    const loadRatings = async () => {
        const { data } = await axios.get(`${API}/ratings`);
        setRatings(data);
    };

    useEffect(() => {
        if (token) {
            Promise.all([loadBanners(), loadRatings()]).catch(() => {
                localStorage.removeItem("ua_admin_token");
                setToken("");
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setLoggingIn(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            localStorage.setItem("ua_admin_token", data.token);
            setToken(data.token);
            toast.success("Welcome back, admin");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Login failed");
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("ua_admin_token");
        setToken("");
    };

    const upload = async (e) => {
        e.preventDefault();
        if (!form.title) {
            toast.error("A title is required.");
            return;
        }
        if (type === "event" && !file) {
            toast.error("Event banners need an image.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("type", type);
            fd.append("title", form.title);
            fd.append("subtitle", form.subtitle);
            fd.append("event_date", form.event_date);
            fd.append("booking_link", form.booking_link);
            fd.append("badge", form.badge);
            fd.append("price", form.price);
            fd.append("terms", form.terms);
            fd.append("lines", form.lines);
            fd.append("active", "true");
            if (file) fd.append("image", file);
            await axios.post(`${API}/admin/banners`, fd, auth);
            toast.success(type === "offer" ? "Offer published" : "Event banner published");
            setForm(emptyForm);
            setFile(null);
            e.target.reset();
            await loadBanners();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const toggleActive = async (b) => {
        await axios.patch(`${API}/admin/banners/${b.id}`, { active: !b.active }, auth);
        await loadBanners();
    };

    const remove = async (b) => {
        await axios.delete(`${API}/admin/banners/${b.id}`, auth);
        toast.success("Deleted");
        await loadBanners();
    };

    const saveRatings = async () => {
        setSavingRatings(true);
        try {
            await axios.put(`${API}/admin/ratings`, ratings, auth);
            toast.success("Ratings updated on the website");
        } catch {
            toast.error("Could not save ratings.");
        } finally {
            setSavingRatings(false);
        }
    };

    const inputCls = "w-full rounded-2xl border border-[#262055]/15 bg-white px-5 py-3.5 text-[#262055] placeholder:text-[#262055]/40 focus:outline-none focus:ring-2 focus:ring-[#00958C]";

    if (!token) {
        return (
            <div className="min-h-screen bg-[#FFF7C5] flex items-center justify-center px-6" data-testid="admin-login-page">
                <form onSubmit={login} className="w-full max-w-md bg-white rounded-[2rem] p-10 shadow-2xl shadow-[#262055]/10 space-y-5" data-testid="admin-login-form">
                    <img src="/brand/ua-logo-colour.webp" alt="Uncover Avenue" className="h-14 w-auto" data-testid="admin-logo" />
                    <h1 className="text-xl font-bold text-[#262055]">Admin Login</h1>
                    <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" />
                    <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" />
                    <button type="submit" disabled={loggingIn} className="w-full rounded-full bg-[#F0841B] text-white font-semibold py-3.5 hover:brightness-110 transition-[filter] duration-200 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="admin-login-button">
                        {loggingIn && <Loader2 size={16} className="animate-spin" />}
                        {loggingIn ? "Signing in…" : "Sign In"}
                    </button>
                    <a href="/" className="block text-center text-sm text-[#262055]/50 hover:text-[#262055] transition-colors" data-testid="admin-back-home-link">Back to website</a>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FFF7C5] py-12 px-6" data-testid="admin-dashboard">
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-10">
                    <div>
                        <img src="/brand/ua-logo-colour.webp" alt="Uncover Avenue" className="h-14 w-auto" data-testid="admin-logo" />
                        <h1 className="text-3xl font-bold tracking-tight text-[#262055] mt-1" data-testid="admin-dashboard-title">Events & Offers</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="/" className="text-sm text-[#262055]/60 hover:text-[#262055] transition-colors" data-testid="admin-view-site-link">View site</a>
                        <button onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-[#262055] text-white text-sm font-semibold px-5 py-2.5 hover:brightness-125 transition-[filter] duration-200" data-testid="admin-logout-button">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-[2rem] p-8 md:p-10 shadow-xl shadow-[#262055]/5 mb-12" data-testid="ratings-editor">
                    <h2 className="text-lg font-bold text-[#262055] flex items-center gap-2 mb-2">
                        <Star size={18} className="text-[#F0841B] fill-[#F0841B]" /> Guest Ratings
                    </h2>
                    <p className="text-sm text-[#262055]/55 mb-6">These numbers show on the website's reviews section. Update them whenever your Zomato or Google ratings climb.</p>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-3">
                            <p className="text-xs font-bold uppercase tracking-widest text-[#262055]/50">Zomato</p>
                            <input placeholder="Rating (e.g. 4.3)" value={ratings.zomato.rating} onChange={(e) => setRatings({ ...ratings, zomato: { ...ratings.zomato, rating: e.target.value } })} className={inputCls} data-testid="ratings-zomato-rating-input" />
                            <input placeholder="Count (e.g. 1,548 ratings)" value={ratings.zomato.count} onChange={(e) => setRatings({ ...ratings, zomato: { ...ratings.zomato, count: e.target.value } })} className={inputCls} data-testid="ratings-zomato-count-input" />
                        </div>
                        <div className="space-y-3">
                            <p className="text-xs font-bold uppercase tracking-widest text-[#262055]/50">Google</p>
                            <input placeholder="Rating (e.g. 4.8)" value={ratings.google.rating} onChange={(e) => setRatings({ ...ratings, google: { ...ratings.google, rating: e.target.value } })} className={inputCls} data-testid="ratings-google-rating-input" />
                            <input placeholder="Count (e.g. 1,346 reviews)" value={ratings.google.count} onChange={(e) => setRatings({ ...ratings, google: { ...ratings.google, count: e.target.value } })} className={inputCls} data-testid="ratings-google-count-input" />
                        </div>
                    </div>
                    <button onClick={saveRatings} disabled={savingRatings} className="mt-6 rounded-full bg-[#00958C] text-white font-semibold px-9 py-3.5 hover:brightness-110 transition-[filter] duration-200 disabled:opacity-60 flex items-center gap-2" data-testid="ratings-save-button">
                        {savingRatings && <Loader2 size={16} className="animate-spin" />}
                        {savingRatings ? "Saving…" : "Save Ratings"}
                    </button>
                </div>

                <form onSubmit={upload} className="bg-white rounded-[2rem] p-8 md:p-10 shadow-xl shadow-[#262055]/5 space-y-5 mb-12" data-testid="banner-upload-form">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setType("event")}
                            className={`rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-widest transition-colors duration-200 ${type === "event" ? "bg-[#262055] text-white" : "bg-[#262055]/8 text-[#262055]/60 hover:bg-[#262055]/15"}`}
                            data-testid="banner-type-event"
                        >
                            Event Banner
                        </button>
                        <button
                            type="button"
                            onClick={() => setType("offer")}
                            className={`rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-widest transition-colors duration-200 ${type === "offer" ? "bg-[#E50046] text-white" : "bg-[#E50046]/10 text-[#E50046]/70 hover:bg-[#E50046]/20"}`}
                            data-testid="banner-type-offer"
                        >
                            Offer / Deal
                        </button>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                        <input required placeholder={type === "offer" ? "Offer title (e.g. Camikara / Cashmir Cocktails)" : "Event title (e.g. FIFA Final Live at Esak)"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="banner-title-input" />
                        {type === "event" ? (
                            <input placeholder="Date & time (e.g. 19 July, 11 PM – 4 AM)" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className={inputCls} data-testid="banner-date-input" />
                        ) : (
                            <input placeholder="Price (e.g. ₹849 · per cocktail)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} data-testid="offer-price-input" />
                        )}
                    </div>

                    {type === "event" ? (
                        <input placeholder="Short description (optional)" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} data-testid="banner-subtitle-input" />
                    ) : (
                        <>
                            <input placeholder="Badge (e.g. Buy 1 Get 1)" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className={inputCls} data-testid="offer-badge-input" />
                            <textarea
                                placeholder={"What is included, one line per item, e.g.\nCamikara Old Fashioned, Rum, Caramel, Orange & Angostura Bitter\nCashmir Cosmo, Vodka, Cranberry, Orange Liqueur"}
                                value={form.lines}
                                onChange={(e) => setForm({ ...form, lines: e.target.value })}
                                className={`${inputCls} min-h-[100px] resize-none`}
                                data-testid="offer-lines-input"
                            />
                            <input placeholder="Terms (e.g. Cannot be combined with other offers · Taxes applicable)" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className={inputCls} data-testid="offer-terms-input" />
                        </>
                    )}

                    <input placeholder="Booking link (optional, e.g. District / Petpooja link)" value={form.booking_link} onChange={(e) => setForm({ ...form, booking_link: e.target.value })} className={inputCls} data-testid="banner-link-input" />

                    <label className="flex items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#262055]/20 bg-[#FFF7C5]/40 px-6 py-8 cursor-pointer hover:border-[#00958C] transition-colors" data-testid="banner-image-dropzone">
                        <Upload size={18} className="text-[#00958C]" />
                        <span className="text-sm text-[#262055]/70">
                            {file ? file.name : type === "event" ? "Choose banner image (required)" : "Choose an image (optional)"}
                        </span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="banner-image-input" />
                    </label>

                    <button type="submit" disabled={uploading} className="rounded-full bg-[#F0841B] text-white font-semibold px-9 py-3.5 hover:brightness-110 transition-[filter] duration-200 disabled:opacity-60 flex items-center gap-2" data-testid="banner-upload-button">
                        {uploading && <Loader2 size={16} className="animate-spin" />}
                        {uploading ? "Publishing…" : type === "offer" ? "Publish Offer" : "Publish Banner"}
                    </button>
                </form>

                <div className="space-y-5" data-testid="banner-list">
                    {banners.length === 0 && <p className="text-[#262055]/50 text-sm" data-testid="banner-list-empty">Nothing published yet. Add your first event or offer above.</p>}
                    {banners.map((b) => (
                        <div key={b.id} className="bg-white rounded-3xl p-5 shadow-lg shadow-[#262055]/5 flex items-center gap-5" data-testid={`banner-row-${b.id}`}>
                            {b.image_path ? (
                                <img src={`${API}/files/${b.image_path}`} alt={b.title} className="w-24 h-24 rounded-2xl object-cover shrink-0" />
                            ) : (
                                <div className="w-24 h-24 rounded-2xl shrink-0 flex items-center justify-center text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#E50046" }}>Offer</div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-bold text-[#262055] truncate">{b.title}</h3>
                                    <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${b.type === "offer" ? "bg-[#E50046]/10 text-[#E50046]" : "bg-[#262055]/8 text-[#262055]/60"}`} data-testid={`banner-type-label-${b.id}`}>
                                        {b.type === "offer" ? "Offer" : "Event"}
                                    </span>
                                </div>
                                {(b.event_date || b.price) && <p className="text-sm text-[#262055]/60">{b.event_date || b.price}</p>}
                                <span className={`inline-block mt-1.5 text-xs font-bold uppercase tracking-widest ${b.active ? "text-[#00958C]" : "text-[#262055]/40"}`} data-testid={`banner-status-${b.id}`}>
                                    {b.active ? "Live on website" : "Hidden"}
                                </span>
                            </div>
                            <button onClick={() => toggleActive(b)} className="rounded-full border-2 border-[#00958C] text-[#00958C] text-sm font-semibold px-5 py-2 hover:bg-[#00958C] hover:text-white transition-colors duration-200" data-testid={`banner-toggle-${b.id}`}>
                                {b.active ? "Hide" : "Publish"}
                            </button>
                            <button onClick={() => remove(b)} className="p-2.5 text-[#E21B54] hover:bg-[#E21B54]/10 rounded-full transition-colors" aria-label="Delete" data-testid={`banner-delete-${b.id}`}>
                                <Trash2 size={18} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
